import { useState } from 'react';
import { Avatar, Badge, Bar, ConsolePage, DStat, Icon, Panel, QueryState, ListSkeleton, Segmented } from '@/components';
import { useCourses, useRoster, useSurveySummary, useItemAnalysis } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';
import { evalQuestionsFor } from '@/data/evaluation';
import { initialsOf } from '@/lib/format';
import { BigScreenPresenter } from './bigscreen/BigScreenPresenter';
import { SurveyReport } from './SurveyReport';

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

/**
 * Course + survey analytics body — reused by the instructor (/teach/analytics)
 * and admin (/admin/reports). Self-contained (own data hooks, own Download-PDF
 * button + print stylesheet) so either page can just drop it in.
 */
export function CourseAnalyticsReport() {
  const { L } = useLocalized();
  const { data: courses = [] } = useCourses();
  const course = courses[0];
  const courseId = course?.id;
  const rosterQuery = useRoster(courseId);
  const roster = rosterQuery.data ?? [];
  const surveyQuery = useSurveySummary(courseId);
  const survey = surveyQuery.data;
  const evalQs = courseId ? evalQuestionsFor(courseId) : [];
  const [itemKind, setItemKind] = useState<'pre' | 'post'>('pre');
  const [present, setPresent] = useState(false);
  const items = (useItemAnalysis(courseId, itemKind).data ?? []).filter((it) => it.total > 0);

  const enrolled = roster.length;
  const attended = roster.filter((r) => r.attended).length;
  const postScores = roster.filter((r) => r.posttest != null).map((r) => r.posttest as number);
  const preScores = roster.filter((r) => r.pretest != null).map((r) => r.pretest as number);
  const postPass = postScores.filter((s) => s >= 80).length;
  const passRate = postScores.length ? Math.round((postPass / postScores.length) * 100) : 0;
  const certIssued = roster.filter((r) => r.cert === 'issued').length;
  const avgPost = Math.round(mean(postScores));
  const avgPre = Math.round(mean(preScores));

  const answers = survey?.answers ?? [];
  const comments = survey?.comments ?? [];
  const qAvg = (qid: string) => mean(answers.map((a) => a[qid]).filter((v): v is number => typeof v === 'number'));
  const overallAvg = answers.length ? mean(evalQs.map((q) => qAvg(q.id))) : 0;
  const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(1) : '—');

  return (
    <QueryState query={rosterQuery} skeleton={<ListSkeleton rows={6} />} isEmpty={() => !course} emptyIcon="chart" emptyTitle="No course to analyse yet">
      {() => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {present && courseId && course && (
            <BigScreenPresenter courseId={courseId} titleTh={course.title.th} onClose={() => setPresent(false)} />
          )}
          {/* Print-only formal report for the dean — replaces the page on print. */}
          {course && (
            <SurveyReport
              course={course}
              count={survey?.count ?? 0}
              overallAvg={overallAvg}
              rows={evalQs.map((q) => ({ label: q.prompt, avg: qAvg(q.id) }))}
              comments={comments}
            />
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{course?.title.en} · {course?.code} · {course?.seminar}</div>
            <div style={{ display: 'flex', gap: 8 }} className="no-print">
              <button className="btn btn-ghost btn-sm" onClick={() => setPresent(true)}><Icon name="scan" size={15} /> Show on big screen · แสดงบนจอใหญ่</button>
              <button className="btn btn-primary btn-sm" onClick={() => window.print()}><Icon name="download" size={15} /> Survey report · รายงานผลประเมิน</button>
            </div>
          </div>

          {/* Course summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
            <DStat label="Enrolled" value={`${enrolled}${course?.capacity ? ` / ${course.capacity}` : ''}`} icon="users" tone="var(--spark)" sub="seats filled" />
            <DStat label="Attended" value={String(attended)} icon="qr" tone="var(--slate)" sub={enrolled ? `${Math.round((attended / enrolled) * 100)}% of enrolled` : '—'} />
            <DStat label="Pass rate" value={`${passRate}%`} icon="chart" tone="var(--sage)" sub={`${postPass} of ${postScores.length} passed`} />
            <DStat label="Avg score" value={`${avgPost}%`} icon="award" tone="var(--ochre)" sub={`pre ${avgPre}% → post ${avgPost}%`} />
            <DStat label="Certificates" value={String(certIssued)} icon="award" tone="var(--sage)" sub="issued" />
          </div>

          {/* Survey */}
          <Panel title="Survey · แบบประเมิน" action={<Badge kind="slate">{survey?.count ?? 0} responses</Badge>} pad={false}>
            {surveyQuery.isError ? (
              <div style={{ padding: 18, fontSize: 13, color: 'var(--ink-3)' }}>Couldn’t load survey responses.</div>
            ) : (survey?.count ?? 0) === 0 ? (
              <div style={{ padding: 18, fontSize: 13.5, color: 'var(--ink-3)' }}>No survey responses yet.</div>
            ) : (
              <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 28, fontWeight: 600, color: 'var(--sage-deep)' }}>{fmt(overallAvg)}</span>
                  <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>/ 5 overall satisfaction</span>
                </div>
                {evalQs.map((q) => {
                  const avg = qAvg(q.id);
                  return (
                    <div key={q.id} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span className="th" style={{ flex: 1, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.4 }}>{L(q.prompt)}</span>
                      <div style={{ width: 120, flex: 'none' }}><Bar value={(avg / 5) * 100} /></div>
                      <span className="u-mono" style={{ width: 34, fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'right', flex: 'none' }}>{fmt(avg)}</span>
                    </div>
                  );
                })}
                {comments.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>Comments · ความคิดเห็น</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {comments.map((c, i) => (
                        <div key={i} className="th" style={{ fontSize: 13, color: 'var(--ink-2)', padding: '9px 12px', background: 'var(--card-2)', borderRadius: 9, lineHeight: 1.5 }}>“{c}”</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Panel>

          {/* Most-missed questions — what to reteach */}
          <Panel
            title="Most-missed questions · ข้อที่ตอบผิดบ่อย"
            action={<Segmented value={itemKind} onChange={setItemKind} options={[{ value: 'pre' as const, label: 'Pre' }, { value: 'post' as const, label: 'Post' }]} />}
            pad={false}
          >
            {items.length === 0 ? (
              <div style={{ padding: 18, fontSize: 13.5, color: 'var(--ink-3)' }}>No {itemKind}-test attempts yet — this fills in as learners take the {itemKind}-test.</div>
            ) : (
              <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {items.slice(0, 10).map((it) => (
                  <div key={it.id} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span className="u-mono" style={{ fontSize: 11.5, color: 'var(--ink-4)', width: 26, flex: 'none' }}>#{it.ord}</span>
                    <span className="th" style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.4 }}>{it.stem}</span>
                    <div style={{ width: 80, flex: 'none' }}><Bar value={it.wrongPct} /></div>
                    <span className="u-mono" style={{ width: 72, fontSize: 12, textAlign: 'right', flex: 'none', fontWeight: 600, color: it.wrongPct >= 50 ? 'var(--danger)' : it.wrongPct >= 25 ? 'var(--ochre)' : 'var(--ink-3)' }}>{it.wrongPct}% · {it.wrong}/{it.total}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* Participants */}
          <div>
          <Panel title={`Participants · ${enrolled}`} pad={false}>
            <div style={{ overflowX: 'auto' }}>
              <table className="pax-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--ink-3)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    <th style={{ padding: '10px 14px', fontWeight: 500 }}>Name</th>
                    <th style={{ padding: '10px 8px', fontWeight: 500 }}>Unit</th>
                    <th style={{ padding: '10px 8px', fontWeight: 500, textAlign: 'center' }}>Pre</th>
                    <th style={{ padding: '10px 8px', fontWeight: 500, textAlign: 'center' }}>Post</th>
                    <th style={{ padding: '10px 8px', fontWeight: 500, textAlign: 'center' }}>Attend</th>
                    <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>Cert</th>
                  </tr>
                </thead>
                <tbody>
                  {roster.map((r, i) => (
                    <tr key={r.userId ?? i} style={{ borderTop: '1px solid var(--line)' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
                          <Avatar initials={initialsOf(r.name.split(' ')[0], r.name.split(' ')[1] ?? '')} size={26} tone={r.tone ?? 'slate'} photoUrl={r.photoUrl} />
                          <span style={{ fontWeight: 500 }}>{r.name || r.th}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 8px', color: 'var(--ink-3)' }}>{r.unit || '—'}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', color: 'var(--ink-2)' }}>{r.pretest != null ? `${r.pretest}%` : '—'}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, color: r.posttest != null ? (r.posttest >= 80 ? 'var(--sage-deep)' : 'var(--danger)') : 'var(--ink-3)' }}>{r.posttest != null ? `${r.posttest}%` : '—'}</td>
                      <td style={{ padding: '10px 8px' }}><div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{r.attended ? <Icon name="check" size={16} style={{ color: 'var(--sage)' }} /> : <span style={{ color: 'var(--ink-4)' }}>—</span>}</div></td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        {r.cert === 'issued' ? <Badge kind="sage" dot>Issued</Badge> : r.cert === 'ready' ? <Badge kind="ochre" dot>Ready</Badge> : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          </div>

          <style>{`
            .pax-table td, .pax-table th { vertical-align: middle; }
          `}</style>
        </div>
      )}
    </QueryState>
  );
}

export function InstructorAnalytics() {
  return (
    <ConsolePage title="Analytics" th="วิเคราะห์ผล" crumbs="TEACHING" back="/teach">
      <CourseAnalyticsReport />
    </ConsolePage>
  );
}
